import React, { useState } from 'react'

import Tippy from '@tippyjs/react/headless'
import { useRouter } from 'next/router'

import { useCommonTx } from '~/hooks/useTranslationForNamespace'
import ChevronRight from '~/images/icons/SystemOutlined/ChevronRight.svg'
import CloseIcon from '~/images/icons/SystemOutlined/Close.svg'
import InfoCircle from '~/images/icons/SystemOutlined/InfoCircle.svg'
import LockIcon from '~/images/icons/SystemOutlined/Lock.svg'
import { Button } from '~/ui/Button/Button'

type StatsCardInfoTooltipProps = {
  title: React.ReactNode
  tooltip: React.ReactNode
  tooltipFooter: React.ReactNode
  onShowCallback?: () => void
}
export const StatsCardInfoTooltip = ({ title, tooltip, tooltipFooter, onShowCallback }: StatsCardInfoTooltipProps) => {
  const [visible, setVisible] = useState(false)
  const show = () => {
    setVisible(true)
    onShowCallback?.()
  }
  const hide = () => setVisible(false)

  return (
    <Tippy
      visible={visible}
      interactive={true}
      onClickOutside={hide}
      placement='bottom-start'
      render={(attrs) => (
        <div {...attrs}>
          <div className='w-full max-w-[390px] flex flex-col bg-surface-default shadow-drop-shadow-2 rounded-lg'>
            <div className='flex flex-col p-6'>
              <div className='flex flex-row items-center justify-between'>
                <div className='text-subheading-1'> {title}</div>
                <CloseIcon title='lpi' className='w-7 h-7 cursor-pointer' onClick={hide} />
              </div>
              <hr className='border-border-subdued my-6 w-full' />
              <div className='flex flex-col text-text-subdued'>{tooltip}</div>
            </div>
            <div className=' text-text-subdued border-t border-border-subdued bg-neutral-cool-98 flex flex-col p-6 pt-4'>
              {tooltipFooter}
            </div>
          </div>
        </div>
      )}
    >
      <div onClick={visible ? hide : show} onKeyDown={visible ? hide : show} role='button' tabIndex={0}>
        <InfoCircle title='lpi' className='text-text-disabled w-7 h-7' />
      </div>
    </Tippy>
  )
}

type StatsCardProps = {
  title?: React.ReactNode
  titleAction?: React.ReactNode
  footerTitle?: string
  footerAction?: React.ReactNode
  children: React.ReactNode
}
export const StatsCard = ({ title, titleAction, footerTitle, footerAction, children }: StatsCardProps) => {
  return (
    <section className='w-full shadow-drop-shadow-1 border border-border-subdued bg-surface-default rounded-lg overflow-hidden'>
      <div className='p-4 pl-6 flex flex-row items-center justify-between border-b border-border-subdued'>
        <div className='text-micro-text-1 uppercase text-text-subdued'>{title}</div>
        {titleAction}
      </div>
      {children}
      {footerTitle || footerAction ? (
        <div className='p-4 pl-6 flex flex-row items-center justify-between border-t border-border-subdued'>
          <div className='text-micro-text-1 uppercase text-text-subdued'>{footerTitle}</div>
          {footerAction}
        </div>
      ) : null}
    </section>
  )
}

export const ViewAllButton = ({ onClick }: { onClick: () => void }) => {
  const t = useCommonTx('common')
  return (
    <button onClick={onClick} className='flex gap-1 items-center'>
      <div className='text-micro-text-1 uppercase text-text-subdued'>{t('viewAll')}</div>
      <ChevronRight title='right' className='h-7 w-7' />
    </button>
  )
}

export const LockedCard = () => {
  const t = useCommonTx('stats')
  return (
    <div className='w-full h-[156px] flex flex-col items-center justify-center text-center'>
      <LockIcon title='lock' className='w-10 h-10 mb-2' />
      <div className='text-body-text-3 font-semibold mb-6'>{t('card.featureAvailableWithPremium')}</div>
    </div>
  )
}

export const NoStatsYet = () => {
  const router = useRouter()
  const t = useCommonTx('common')
  const st = useCommonTx('stats')
  const onGoToGames = () => {
    router.push('/games')
  }
  return (
    <div className='w-full flex flex-col justify-center items-center p-6 text-center bg-neutral-cool-98'>
      <div className='text-body-text-3 font-semibold mb-6'>{st('emptyState.p')}</div>
      <Button kind='primary' onClick={onGoToGames}>
        {t('buttons.goToGames')}
      </Button>
    </div>
  )
}

export const LessStats = () => {
  const router = useRouter()
  const t = useCommonTx('common')
  const st = useCommonTx('stats')
  const onGoToGames = () => {
    router.push('/games')
  }
  return (
    <div className='w-full flex flex-col justify-center items-center p-6 py-9 text-center bg-neutral-cool-98'>
      <div className='text-body-text-3 font-semibold mb-6'>{st('emptyState.moreRanking')}</div>
      <Button kind='primary' onClick={onGoToGames}>
        {t('buttons.goToGames')}
      </Button>
    </div>
  )
}
